import React, { useEffect, useCallback } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUndo: () => void;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, onUndo }) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      return undefined;
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleOverlayKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClose();
    }
    return undefined;
  };

  return (
    <div
      className={'fixed inset-0 flex items-center justify-center z-50'}
      role={'dialog'}
      aria-modal={'true'}
      aria-labelledby={'modal-title'}
    >
      <div
        className={'fixed inset-0 bg-[#232429]/60 backdrop-blur-[2px] animate-modal-fade'}
        onClick={onClose}
        onKeyDown={handleOverlayKeyDown}
        role={'button'}
        tabIndex={0}
        aria-label={'Close modal'}
      />
      <div
        className={'relative min-w-[300px] rounded-2xl bg-white animate-modal-fade'}
        role={'alertdialog'}
        aria-describedby={'modal-description'}
      >
        <div className={'px-[24px] pt-[24px] pb-[32px]'}>
          <div className={'flex flex-col items-center text-center pt-4 pb-6'}>
            <h2
              id={'modal-title'}
              className={'text-[18px] leading-[130%] font-bold text-[#232429] whitespace-pre-line font-pretendard'}
            >
              {'🙂 취소가 완료 되었어요'}
            </h2>
            <p id={'modal-description'} className={'text-[14px] leading-[130%] font-normal text-[#505363] mt-[10px]'}>
              {'요청하신 찔러보기가 취소되었어요'}
            </p>
          </div>
          <div className={'flex flex-col gap-2'}>
            <button
              onClick={onUndo}
              className={
                'w-full max-h-[40px] px-4 py-[16px] rounded-lg bg-[#445EFF] text-[14px] font-medium leading-[85%] text-white flex items-center justify-center'
              }
              aria-label={'찔러보기 취소 되돌리기'}
            >
              {'취소 되돌리기'}
            </button>
            <button
              onClick={onClose}
              className={
                'w-full max-h-[40px] px-4 py-[16px] rounded-lg bg-[#ECECF3] text-[14px] font-medium leading-[85%] text-[#505363] flex items-center justify-center'
              }
              aria-label={'모달 닫기'}
            >
              {'닫기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
